import React, { useState, useMemo } from 'react';
import { Search, Calendar, User, TrendingUp, AlertCircle, X, ArrowUpDown } from 'lucide-react';

// Sample data matching your API format
// const apiData = [
//   {"SALES MAN":"","GROSS SALE":31335.26,"DISC":0.0,"NET SALES":31335.26,"COST":19238.6,"PROFIT":10604.5,"%AGE":35.5341,"VAT AMT":1492.16},
//   {"SALES MAN":"ABDUL AZEEM","GROSS SALE":3003.0,"DISC":0.0,"NET SALES":3003.0,"COST":1290.3,"PROFIT":1569.7,"%AGE":54.8846,"VAT AMT":143.0},
//   {"SALES MAN":"ABDULLA","GROSS SALE":5328.75,"DISC":0.0,"NET SALES":5328.75,"COST":2870.088,"PROFIT":2204.912,"%AGE":43.4465,"VAT AMT":253.75},
//   {"SALES MAN":"AJEEB","GROSS SALE":405660.92,"DISC":9098.91,"NET SALES":396562.01,"COST":168771.155,"PROFIT":208906.935,"%AGE":55.3134,"VAT AMT":18883.92},
//   {"SALES MAN":"ASIF","GROSS SALE":619.36,"DISC":2.85,"NET SALES":616.51,"COST":239.9,"PROFIT":347.25,"%AGE":59.1416,"VAT AMT":29.36},
//   {"SALES MAN":"BIJU THOMAS","GROSS SALE":165166.63,"DISC":339.0,"NET SALES":164827.63,"COST":70304.382,"PROFIT":86674.308,"%AGE":55.214,"VAT AMT":7848.94},
//   {"SALES MAN":"FAHID","GROSS SALE":1261.04,"DISC":0.19,"NET SALES":1260.85,"COST":379.35,"PROFIT":821.46,"%AGE":68.4088,"VAT AMT":60.04},
//   {"SALES MAN":"FASIL","GROSS SALE":22103.6,"DISC":0.0,"NET SALES":22103.6,"COST":8839.745,"PROFIT":12211.305,"%AGE":58.008,"VAT AMT":1052.55},
//   {"SALES MAN":"FIROZ","GROSS SALE":108860.43,"DISC":10308.19,"NET SALES":98552.24,"COST":44859.821,"PROFIT":48999.449,"%AGE":52.2052,"VAT AMT":4692.97},
//   {"SALES MAN":"HABEEB","GROSS SALE":155238.22,"DISC":3067.64,"NET SALES":152170.58,"COST":81215.436,"PROFIT":63708.924,"%AGE":43.9601,"VAT AMT":7246.22},
//   {"SALES MAN":"OFFICE","GROSS SALE":147566.38,"DISC":0.0,"NET SALES":147566.38,"COST":193397.802,"PROFIT":-52858.402,"%AGE":-37.611,"VAT AMT":7026.98},
//   {"SALES MAN":"Thasnima","GROSS SALE":145139.58,"DISC":3503.35,"NET SALES":141636.23,"COST":35190.433,"PROFIT":99701.217,"%AGE":73.912,"VAT AMT":6744.58}
// ];

export default function Salesmanprofit({apiData  = [] }) {
  // State variables
  const [searchQuery, setSearchQuery] = useState('');
  const [fromDate, setFromDate] = useState('2026-01-01');
  const [toDate, setToDate] = useState('2026-12-31');
  const [selectedUser, setSelectedUser] = useState(apiData ? apiData[3] : null); // Default to AJEEB for desktop view
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [sortBy, setSortBy] = useState('NET SALES'); // 'NET SALES' or 'PROFIT'

  // Clean data and normalize empty strings
  const processedData = useMemo(() => {
    return apiData?.map(item => ({
      ...item,
      "SALES MAN": item["SALES MAN"].trim() === "" ? "Direct / Unassigned" : item["SALES MAN"].trim()
    }));
  }, []);

  // Filter and Sort Logic
  const filteredData = useMemo(() => {
    return processedData
      ?.filter(item => item["SALES MAN"].toLowerCase().includes(searchQuery.toLowerCase()))
      ?.sort((a, b) => b[sortBy] - a[sortBy]);
  }, [processedData, searchQuery, sortBy]);

  // Global aggregate metrics calculation
  const totalMetrics = useMemo(() => {
    return processedData?.reduce((acc, curr) => {
      acc.sales += curr["NET SALES"];
      acc.profit += curr["PROFIT"];
      return acc;
    }, { sales: 0, profit: 0 });
  }, [processedData]);

  // Helper formatting values cleanly
  const formatCurrency = (val) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val);

  // Helper mapping margin percentage to color indicators
  const getMarginColor = (pct) => {
    if (pct < 0) return 'bg-red-500 text-red-500 border-red-200 pink-danger';
    if (pct < 40) return 'bg-amber-500 text-amber-500 border-amber-200';
    return 'bg-emerald-500 text-emerald-500 border-emerald-200';
  };

  const handleRowSelect = (user) => {
    setSelectedUser(user);
    if (window.innerWidth < 1024) { // Trigger mobile detail layout view drawer on small screens
      setIsMobileModalOpen(true);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800 flex flex-col antialiased">
      
      {/* 1. TOP BAR & DATE FILTERS */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm transition-all">
        <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-gray-900">Performance Analytics</h1>
            <p className="text-xs text-gray-500">Real-time revenue monitoring</p>
          </div>
          
          {/* Date Picker Section */}
          <div className="flex items-center gap-2 bg-gray-50 p-1.5 rounded-lg border border-gray-200 self-start md:self-auto w-full md:w-auto">
            <div className="flex items-center gap-1.5 bg-white px-2 py-1 rounded md:shadow-xs w-full md:w-auto">
              <Calendar size={14} className="text-gray-400 shrink-0" />
              <input 
                type="date" 
                value={fromDate} 
                onChange={(e) => setFromDate(e.target.value)}
                className="text-xs font-medium focus:outline-none bg-transparent w-full"
              />
            </div>
            <span className="text-xs text-gray-400 px-1">to</span>
            <div className="flex items-center gap-1.5 bg-white px-2 py-1 rounded md:shadow-xs w-full md:w-auto">
              <Calendar size={14} className="text-gray-400 shrink-0" />
              <input 
                type="date" 
                value={toDate} 
                onChange={(e) => setToDate(e.target.value)}
                className="text-xs font-medium focus:outline-none bg-transparent w-full"
              />
            </div>
          </div>
        </div>
      </header>

      {/* MAIN CONTAINER */}
      <main className="max-w-7xl mx-auto w-full px-4 py-4 flex-1 grid grid-cols-1 lg:grid-cols-12 gap-4 h-[calc(100vh-140px)] lg:h-[calc(100vh-90px)] overflow-hidden">
        
        {/* 2. MASTER PANEL (Left Sidebar Column) */}
        <section className="lg:col-span-5 flex flex-col bg-white rounded-xl border border-gray-200 overflow-hidden shadow-xs">
          
          {/* Search and Quick Filters */}
          <div className="p-3 border-b border-gray-100 space-y-2 bg-gray-50/50">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search salesperson..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
              />
            </div>
            
            {/* Sorting Action Toggles */}
            <div className="flex items-center justify-between text-xs pt-1">
              <span className="text-gray-500 font-medium">Ranking List ({filteredData?.length})</span>
              <button 
                onClick={() => setSortBy(prev => prev === 'NET SALES' ? 'PROFIT' : 'NET SALES')}
                className="flex items-center gap-1 text-blue-600 font-semibold hover:underline"
              >
                <ArrowUpDown size={12} />
                Sorting by: {sortBy === 'NET SALES' ? 'Net Sales' : 'Margin Profit'}
              </button>
            </div>
          </div>

          {/* Infinite Scrollable Track List Container */}
          <div className="flex-1 overflow-y-auto divide-y divide-gray-100 dynamic-scroll">
            
          {filteredData?.map((item, idx) => {
  const isSelected = selectedUser["SALES MAN"] === item["SALES MAN"];
  
  // 1. Calculate max profit benchmark safely
  const maxProfitVal = Math.max(...processedData.map(o => o["PROFIT"]));
  
  // 2. Derive width. If profit is negative, clamp it to 6% so the red block stays visible.
  const relativeBarWidth = item["PROFIT"] > 0 
    ? (item["PROFIT"] / maxProfitVal) * 100 
    : 6; 

  return (
    <div
      key={idx}
      onClick={() => handleRowSelect(item)}
      className={`p-3.5 transition-all cursor-pointer flex flex-col gap-2 select-none ${
        isSelected ? 'bg-blue-50/70 border-l-4 border-blue-600' : 'hover:bg-gray-50/80'
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-gray-400 w-5">#{idx + 1}</span>
          <span className="text-sm font-semibold tracking-tight text-gray-900 truncate max-w-[160px] sm:max-w-none">
            {item["SALES MAN"]}
          </span>
        </div>
        {/* Changed right-side focus metric to Profit */}
        <div className="text-right">
          <span className={`text-sm font-bold ${item["PROFIT"] < 0 ? 'text-red-600' : 'text-gray-900'}`}>
            {formatCurrency(item["PROFIT"])}
          </span>
          <p className="text-[10px] text-gray-400 uppercase font-bold tracking-wider">Net Profit</p>
        </div>
      </div>

      {/* Context Micro-Visualization Progress Bar */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span className={`px-1.5 py-0.25 text-[10px] rounded font-bold border ${
            item["%AGE"] < 0 ? 'bg-red-50 border-red-200 text-red-600' : 'bg-gray-100 border-gray-200'
          }`}>
            Margin: {item["%AGE"].toFixed(1)}%
          </span>
          <span className="text-gray-500">
            Sales: {formatCurrency(item["NET SALES"])}
          </span>
        </div>
        
        {/* Track Bar Background */}
        <div className="w-full bg-gray-100 h-1.5 rounded-full overflow-hidden">
          <div 
            className={`h-full rounded-full transition-all duration-500 ${getMarginColor(item["%AGE"]).split(' ')[0]}`}
            style={{ width: `${Math.max(relativeBarWidth, 4)}%` }}
          />
        </div>
      </div>
    </div>
  );
})}
          </div>
        </section>

        {/* 3. DETAIL PANEL (Desktop View Right Panel Block) */}
        <section className="hidden lg:col-span-7 lg:flex flex-col bg-white rounded-xl border border-gray-200 overflow-y-auto p-6 shadow-xs space-y-6">
          {selectedUser ? (
            <>
              {/* Profile Card Summary Header */}
              <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-50 text-blue-600 rounded-xl">
                    <User size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{selectedUser["SALES MAN"]}</h2>
                    <span className="text-xs text-gray-400">Detailed Structural Account Breakdown</span>
                  </div>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-bold border flex items-center gap-1.5 ${
                  selectedUser["%AGE"] < 0 ? 'bg-red-50 border-red-200 text-red-600' : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                }`}>
                  {selectedUser["%AGE"] < 0 ? <AlertCircle size={16} /> : <TrendingUp size={16} />}
                  Margin efficiency: {selectedUser["%AGE"].toFixed(2)}%
                </div>
              </div>

              {/* Individual Core Performance KPI Metric Cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
                  <span className="text-xs text-gray-400 font-medium block mb-1">Gross Invoiced Sale</span>
                  <span className="text-lg font-bold text-gray-900">{formatCurrency(selectedUser["GROSS SALE"])}</span>
                </div>
                <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
                  <span className="text-xs text-gray-400 font-medium block mb-1">Applied Discounts</span>
                  <span className={`text-lg font-bold ${selectedUser["DISC"] > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                    -{formatCurrency(selectedUser["DISC"])}
                  </span>
                </div>
                <div className="bg-gray-50 border border-gray-100 rounded-xl p-4">
                  <span className="text-xs text-gray-400 font-medium block mb-1">Net Base Realized</span>
                  <span className="text-lg font-bold text-blue-600">{formatCurrency(selectedUser["NET SALES"])}</span>
                </div>
              </div>

              {/* Detailed Waterfall Ledger Grid List */}
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 font-bold text-xs uppercase tracking-wider text-gray-500">
                  Operational Balance sheet Ledger
                </div>
                <div className="divide-y divide-gray-100 text-sm">
                  <div className="px-4 py-3 flex justify-between">
                    <span className="text-gray-500">Cost of Goods Sold (COGS)</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(selectedUser["COST"])}</span>
                  </div>
                  <div className="px-4 py-3 flex justify-between bg-gray-50/30">
                    <span className="text-gray-500">Government Valued Added Tax (VAT)</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(selectedUser["VAT AMT"])}</span>
                  </div>
                  <div className={`px-4 py-4 flex justify-between items-center text-base font-bold ${
                    selectedUser["PROFIT"] < 0 ? 'bg-red-50 text-red-700' : 'bg-emerald-50/50 text-emerald-800'
                  }`}>
                    <span>Net Take-Home Yield Profit</span>
                    <span>{formatCurrency(selectedUser["PROFIT"])}</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-gray-400">
              <User size={48} className="stroke-1 mb-2" />
              <p className="text-sm">Select a salesperson from the left index list to monitor metrics.</p>
            </div>
          )}
        </section>
      </main>

      {/* 4. MOBILE DETAIL BOTTOM DRAWER FRAME */}
      {isMobileModalOpen && selectedUser && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-50 flex flex-col justify-end transition-opacity duration-300">
          {/* Backdrop Tap Target Close */}
          <div className="flex-1" onClick={() => setIsMobileModalOpen(false)} />
          
          {/* Animated Drawer Body */}
          <div className="bg-white rounded-t-2xl max-h-[85vh] overflow-y-auto p-4 space-y-5 shadow-2xl animate-slide-up">
            <div className="flex items-center justify-between pb-2 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                  <User size={18} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 truncate max-w-[220px]">{selectedUser["SALES MAN"]}</h3>
              </div>
              <button 
                onClick={() => setIsMobileModalOpen(false)}
                className="p-1 rounded-full bg-gray-100 text-gray-500 hover:bg-gray-200"
              >
                <X size={18} />
              </button>
            </div>

            {/* Performance Metric Stacks */}
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                <span className="text-gray-400 font-medium block mb-0.5">Net Revenue</span>
                <span className="text-base font-bold text-gray-900">{formatCurrency(selectedUser["NET SALES"])}</span>
              </div>
              <div className={`p-3 rounded-xl border ${
                selectedUser["PROFIT"] < 0 ? 'bg-red-50 border-red-100 text-red-700' : 'bg-emerald-50 border-emerald-100 text-emerald-800'
              }`}>
                <span className="opacity-70 font-medium block mb-0.5">Net Profit</span>
                <span className="text-base font-bold">{formatCurrency(selectedUser["PROFIT"])}</span>
              </div>
            </div>

            {/* Complete Data Matrix List */}
            <div className="bg-gray-50 rounded-xl p-3 space-y-2.5 text-xs">
              <div className="flex justify-between"><span className="text-gray-500">Gross Sales</span><span className="font-semibold text-gray-900">{formatCurrency(selectedUser["GROSS SALE"])}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Discounts Allowed</span><span className="font-semibold text-amber-600">-{formatCurrency(selectedUser["DISC"])}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Cost Value</span><span className="font-semibold text-gray-900">{formatCurrency(selectedUser["COST"])}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Margin Weight</span><span className="font-bold text-gray-900">{selectedUser["%AGE"].toFixed(2)}%</span></div>
              <div className="flex justify-between pt-2 border-t border-gray-200"><span className="text-gray-500">VAT Collected</span><span className="font-semibold text-gray-900">{formatCurrency(selectedUser["VAT AMT"])}</span></div>
            </div>

            <button 
              onClick={() => setIsMobileModalOpen(false)}
              className="w-full py-2.5 bg-gray-900 text-white font-semibold rounded-xl text-xs shadow-xs"
            >
              Close Detailed Breakdown
            </button>
          </div>
        </div>
      )}
    </div>
  );
}